import {
  Badge,
  Button,
  Flex,
  FormatDeliveryDate,
  Icon,
  IconButton,
  Text,
  Tooltip,
  TyreInfoBox,
  Row,
  Col,
} from '@wyz/ui';
import {
  DateUtils,
  mapTyreIntoInfoBox,
  OFFER_TYPE,
  ORDER_TYPE_CODE,
  useBoolean,
} from '@wyz/utils';
import clsx from 'clsx';
import { DebouncedFunc, isNil } from 'lodash-es';
import { useManageCart, useManageSearch } from '~/purchase/api';
import {
  PurchaseAddToCartButton,
  PurchaseDetailModal,
  PurchaseOfferDescriptionCell,
} from '~/purchase/components';
import { MIN_QUANTITY } from '~/purchase/helpers';

import { CUSTOMER_LEVEL, ITyreSupplier } from '@wyz/types';
import { IBaseTyreOffer, IOfferMetadata } from '~/purchase/types';
import { useFormContext } from 'react-hook-form';
import { NumberField, PrivateComponent } from '~/common/components';
import { Trans, useConfig, useI18n, useLocale } from '~/common/context';
import { FEATURES, PurchaseErrorCodes, ROLES } from '~/common/helpers';
import { useAccessControl, usePurchaseErrorTranslation } from '~/common/hooks';
import { IUserCustomer } from '~/common/types';
import { SEARCH_TYPE_ENUM } from '~/purchase/schemas';
import { IOffer } from '~/purchase/types';

type Props = {
  customer: IUserCustomer;
  onUpdateQuantity: (
    supplier: ITyreSupplier,
  ) => DebouncedFunc<(newQuantity?: number | null) => void>;
  isAdding: boolean;
  showCommon: boolean;
  offersCount: number;
  offerIndex: number;
  product: Pick<
    IBaseTyreOffer,
    | 'status'
    | 'totalAvailableQuantity'
    | 'tyre'
    | 'tyreCreatedAt'
    | 'alternativeCount'
    | 'canOrder'
    | 'customerStock'
    | 'containsFallbackOffers'
    | 'retryable'
    | 'proximityStocks'
  >;
  requestedQuantity: number;
  offer: {
    supplier: ITyreSupplier;
    offerMetadata: IOfferMetadata;
  };
  onRefreshTyre: (ean: string) => Promise<Array<IOffer>>;
  showStockDetails: boolean;
  toggleShowStockDetails: () => void;
};

function OfferSuccessRow({
  product,
  isAdding,
  requestedQuantity,
  onUpdateQuantity,
  offer,
  showCommon,
  offersCount,
  offerIndex,
  customer,
  onRefreshTyre,
  showStockDetails,
  toggleShowStockDetails,
}: Props) {
  const { t } = useI18n();
  const [detailOpened, detailHandlers] = useBoolean();
  const acl = useAccessControl();
  const { resolveErrorKey } = usePurchaseErrorTranslation();
  const { cdnUrl } = useConfig();
  const locale = useLocale();
  const searchContext = {
    customerId: customer.id,
    searchType:
      customer.level === CUSTOMER_LEVEL.N1
        ? SEARCH_TYPE_ENUM.DISTRIBUTION
        : SEARCH_TYPE_ENUM.REDISTRIBUTION,
  };

  const manageSearch = useManageSearch<IBaseTyreOffer>({
    context: searchContext,
    refreshEan: { enabled: true },
  });
  const methods = useFormContext();

  const hasLocalOrProximityStock =
    (product.customerStock ?? 0) > 0 || product.proximityStocks.length > 0;

  const supplier = offer.supplier;
  const cart = useManageCart({
    context: { customerId: customer.id },
    get: { enabled: true },
    patch: { enabled: true },
    remove: { enabled: true },
  });
  const cartItems =
    cart.get.data?.data.items.filter(
      (item) => item.tyre.ean === product.tyre.ean,
    ) || [];
  const tyreOrderedQuantity = cartItems.reduce(
    (acc, curr) => acc + curr.quantity,
    0,
  );
  const isRedistributionEnabled = acl.isEnabledFeatures([
    FEATURES.REDISTRIBUTION,
  ]);
  const isInRedistribution =
    isRedistributionEnabled && customer.level > CUSTOMER_LEVEL.N1;

  const isPartiallyAvailable =
    product.totalAvailableQuantity > 0 &&
    product.totalAvailableQuantity < requestedQuantity;

  return (
    <tr
      className={clsx('text-nowrap fs-xs position-relative', {
        subhead: showCommon,
        main: offerIndex === offersCount - 1,
      })}
    >
      {showCommon && (
        <td id='product' rowSpan={offersCount}>
          <TyreInfoBox
            imageBaseUrl={cdnUrl}
            t={t}
            tyre={mapTyreIntoInfoBox(
              product.tyre,
              customer.settingTemplate?.generalSetting.tyreIdentifierFields ??
                undefined,
            )}
            direction='horizontal'
            mode='FULL'
          />
        </td>
      )}
      {showCommon && (
        <td id='detail' rowSpan={offersCount} className='p-0'>
          <PurchaseOfferDescriptionCell
            product={product}
            onDetail={detailHandlers.setTrue}
          />
        </td>
      )}
      {showCommon && (
        <td
          id='bf-price'
          rowSpan={offersCount}
          className='text-center align-middle'
        >
          <Text heading='xs' weight='semi-bold' id='bf-price-value'>
            {locale.formatters.money(product.tyre.priceFrame.price, {
              currency: product.tyre.priceFrame.currencyIsoCode,
              fallback: '/',
            })}
          </Text>
        </td>
      )}
      {showCommon && customer?.hasInternalStocks && (
        <td
          className='text-center align-middl'
          rowSpan={offersCount}
          id='customer-stock'
        >
          {hasLocalOrProximityStock ? (
            <>
              <Text heading='xs' weight='semi-bold'>
                {product.customerStock ?? '/'}
              </Text>
              <Icon
                id={`stock-trigger-${product.tyre.ean}`}
                icon='info-circle'
                className='cursor-pointer mx-auto mt-2'
                onClick={toggleShowStockDetails}
              />
            </>
          ) : (
            <Text heading='xs' weight='semi-bold'>
              {'/'}
            </Text>
          )}
        </td>
      )}
      {showStockDetails && showCommon && (
        <td id='stock-details' colSpan={5} rowSpan={offersCount}>
          <div className='py-1 px-3'>
            <Flex alignItems='center' justifyContent='between'>
              <Text heading='normal' weight='semi-bold' color='tertiary'>
                <Trans i18nKey='app.purchase.table.stock_details.title' />
              </Text>
              <div
                className='rounded rounded-circle bg-light p-2 cursor-pointer border border-1'
                onClick={toggleShowStockDetails}
              >
                <Icon icon='close' />
              </div>
            </Flex>
            <Row>
              <Col>
                <Text heading='sm' color='primary' weight='semi-bold'>
                  <Trans i18nKey='app.purchase.table.stock_details.local' />
                  {' :'}
                </Text>
              </Col>
              <Col>
                <Text heading='sm' color='primary' weight='semi-bold'>
                  {product.customerStock ?? 0}
                </Text>
              </Col>
            </Row>
            {product.proximityStocks.map((stock, stockIndex) => (
              <Row key={stockIndex}>
                <Col>
                  <Text heading='sm' color='primary' weight='semi-bold'>
                    {stock.supplierName}
                    {' :'}
                  </Text>
                </Col>
                <Col>
                  <Text heading='sm' color='primary' weight='semi-bold'>
                    {stock.quantity}
                  </Text>
                </Col>
              </Row>
            ))}
          </div>
        </td>
      )}
      {!showStockDetails && (
        <>
          <td id='supplier' className='p-0 no-border'>
            <Flex
              direction='column'
              justifyContent='around'
              className='list-unstyled h-100 w-100'
              wrap='wrap'
            >
              <div
                className={clsx('w-100 px-1 d-flex align-items-center', {
                  'multi': !showCommon,
                  'fw-bold text-orange-1': Boolean(offer.offerMetadata.isJ0),
                })}
              >
                <Text
                  heading='xs'
                  weight='semi-bold'
                  color='tertiary'
                  className='text-wrap'
                  id={`supplier-${product.tyre.ean}-${supplier.name}`}
                >
                  <span>{supplier.name}</span>
                </Text>
              </div>
              {customer.level > CUSTOMER_LEVEL.N1 && (
                <div className='m-2'>
                  {offer.offerMetadata.offerType ===
                  OFFER_TYPE.DISTRIBUTOR_PLATFORM ? (
                    <Badge bg='medium-green'>
                      <Text
                        weight='bolder'
                        heading='xs'
                        color='inherit'
                        className='px-2'
                      >
                        <Trans i18nKey='app.purchase.redistribution.in_stock_offer' />
                      </Text>
                    </Badge>
                  ) : (
                    <Badge bg='pumpkin'>
                      <Text
                        weight='bolder'
                        heading='xs'
                        color='inherit'
                        className='px-2'
                      >
                        <Trans i18nKey='app.purchase.redistribution.out_of_stock_offer' />
                      </Text>
                    </Badge>
                  )}
                </div>
              )}
            </Flex>
          </td>
          <td id='delivery' className={'p-0 no-border'}>
            {offer.offerMetadata.isFallback ? (
              <Flex
                alignItems='center'
                justifyContent='center'
                className='gap-2'
              >
                <Text
                  id={`fallback-delivery-${product.tyre.ean}-${supplier.name}-${offerIndex}`}
                  weight='semi-bold'
                >
                  {'/'}
                </Text>
                {offer.offerMetadata.providerErrors.map((error) => (
                  <Tooltip key={error} label={t(resolveErrorKey(error))}>
                    <Icon icon='info-circle' color='secondary' />
                  </Tooltip>
                ))}
                {offer.offerMetadata.providerErrors.length === 0 && (
                  <Tooltip label={t('app.purchase.supplier_timeout')}>
                    <Icon icon='info-circle' color='secondary' />
                  </Tooltip>
                )}
              </Flex>
            ) : (
              <Flex
                direction='column'
                alignItems='center'
                className='list-unstyled h-100 w-100'
                wrap='wrap'
              >
                <div
                  className={clsx(
                    'd-flex w-100 px-1 space-x-1 align-items-center justify-content-center gap-2',
                    {
                      'justify-content-center': Boolean(
                        !offer.offerMetadata.isJ0,
                      ),
                      'multi': !showCommon,
                    },
                  )}
                >
                  <>
                    <Text
                      heading='xs'
                      weight='semi-bold'
                      id={`delivery-${product.tyre.ean}-${supplier.name}-${offerIndex}`}
                      color='secondary'
                    >
                      <FormatDeliveryDate
                        orderType={
                          offer.offerMetadata.isBackOrder
                            ? ORDER_TYPE_CODE.BO
                            : null
                        }
                        backorderContent={
                          <Trans i18nKey='app.common.offer_type.backorder' />
                        }
                        date={offer.offerMetadata.deliveryDate}
                        format={DateUtils.DATE_FORMAT.DM}
                        locale={locale.locale}
                      />
                    </Text>
                    {offer.offerMetadata.isJ0 && (
                      <Icon
                        id='today-delivery-icon'
                        icon={
                          offer.offerMetadata.todayDelivery
                            ? 'today-delivery-wording'
                            : 'morning-delivery-wording'
                        }
                      />
                    )}

                    {customer.settingTemplate?.generalSetting
                      .displayBestDelayTag &&
                      offer.offerMetadata.isBestDelay && (
                        <div className='w-100'>
                          <Badge bg='info' className='text-nowrap'>
                            <Text
                              id={`best-delay-${product.tyre.ean}-${supplier.name}-${offerIndex}`}
                              heading='xxs'
                              weight='semi-bold'
                              color='primary'
                            >
                              <Trans i18nKey='app.purchase.best_delay' />
                            </Text>
                          </Badge>
                        </div>
                      )}
                  </>
                </div>
              </Flex>
            )}
          </td>
          <td id='stock' className='p-0 no-border' align='right'>
            <Flex
              direction='column'
              alignItems='center'
              justifyContent='center'
              className='list-unstyled h-100 w-100'
              wrap='wrap'
            >
              <div className={'w-100 px-1 justify-content-center'}>
                <>
                  {isPartiallyAvailable ? (
                    <Badge bg='info'>
                      {offer.offerMetadata.availableQuantity}
                    </Badge>
                  ) : (
                    <Text heading='sm' weight='semi-bold'>
                      {offer.offerMetadata.availableQuantity}
                    </Text>
                  )}
                </>
              </div>
            </Flex>
          </td>
          <td id='quantity' className='p-0 no-border'>
            <div className='px-1'>
              {offer.offerMetadata.isFallback ? (
                <Tooltip
                  label={
                    <Trans
                      i18nKey={resolveErrorKey(
                        offer.offerMetadata.providerErrors[0],
                      )}
                    />
                  }
                  enabled={offer.offerMetadata.providerErrors.length > 0}
                >
                  <Flex justifyContent='center'>
                    <Icon icon='alert' size='sm' />
                  </Flex>
                </Tooltip>
              ) : (
                <NumberField
                  id={`${supplier.name}-${offerIndex}`}
                  name={`quantity.${offerIndex}`}
                  size='sm'
                  min={MIN_QUANTITY}
                  max={offer.offerMetadata.availableQuantity}
                  disabled={
                    offer.offerMetadata.isFallback ||
                    !offer.offerMetadata.availableQuantity ||
                    cartItems.length > 0 ||
                    (!supplier.orderable && !isInRedistribution) ||
                    !product.canOrder ||
                    !acl.isGranted(
                      [
                        ROLES.ROLE_UPDATE_CART,
                        ROLES.ROLE_UPDATE_REDISTRIBUTION_CART,
                      ],
                      'affirmative',
                    )
                  }
                  onChangeValue={onUpdateQuantity(supplier)}
                  feedbackType='tooltip'
                />
              )}
            </div>
          </td>
          <td id='unit-price' className='p-0 no-border text-wrap' align='right'>
            <div style={{ width: '100%', overflow: 'hidden' }}>
              <Flex direction='column' className='overflow-hidden w-100'>
                <Flex
                  direction='column'
                  justifyContent='around'
                  className='w-100 h-100 '
                  wrap='wrap'
                >
                  <div className={'w-100 px-1'}>
                    {customer.settingTemplate?.generalSetting
                      .displayBestPriceTag &&
                      offer.offerMetadata.isBestPrice && (
                        <div className='w-100'>
                          <Badge bg='info' className='text-nowrap'>
                            <Text
                              id={`offer-${offerIndex}-best-price-tag`}
                              heading='xxs'
                              weight='semi-bold'
                              color='primary'
                            >
                              <Trans i18nKey='app.purchase.best_price' />
                            </Text>
                          </Badge>
                        </div>
                      )}
                    <div className='w-100'>
                      <Text
                        heading='sm'
                        weight='semi-bold'
                        id='unit-price-value'
                      >
                        {!isNil(offer.offerMetadata.displayPrice) &&
                        offer.offerMetadata.displayPrice !== 0
                          ? locale.formatters.money(
                              offer.offerMetadata.displayPrice,
                              {
                                currency:
                                  product.tyre.priceFrame.currencyIsoCode,
                              },
                            )
                          : '-'}
                      </Text>
                      {!isNil(product.tyre.priceFrame.price) && (
                        <Text
                          heading='xxs'
                          weight='semi-bold'
                          color='secondary-gray'
                          id='discount-percentage'
                          className='text-truncate d-block'
                        >
                          {!isNil(offer.offerMetadata.displayPrice) &&
                          offer.offerMetadata.displayPrice !== 0
                            ? t('app.purchase.bf_discount', {
                                price: offer.offerMetadata.discountPercentage,
                              })
                            : '-'}
                        </Text>
                      )}
                    </div>
                  </div>
                </Flex>
                {offer.offerMetadata.isJ0 && (
                  <Flex
                    alignItems='center'
                    justifyContent='end'
                    className='px-1'
                  >
                    <Icon
                      id='today-delivery-icon'
                      size='sm'
                      icon={
                        offer.offerMetadata.todayDelivery
                          ? 'today-delivery'
                          : 'morning-delivery'
                      }
                    />
                    <Badge
                      bg='primary'
                      className='text-nowrap'
                      id='j0-price-badge'
                    >
                      {/* nullish or 0 => free service */}
                      {!offer.offerMetadata.j0Price
                        ? t('app.common.free_service')
                        : locale.formatters.money(offer.offerMetadata.j0Price, {
                            currency: product.tyre.priceFrame.currencyIsoCode,
                            fallback: '-',
                          })}
                    </Badge>
                  </Flex>
                )}
              </Flex>
            </div>
          </td>
        </>
      )}
      {showCommon && (
        <td
          id='action'
          rowSpan={offersCount}
          className='align-middle p-0 text-wrap'
        >
          <Flex
            className='p-1 gap-2'
            direction='column'
            justifyContent='center'
            alignItems='center'
          >
            <>
              {isPartiallyAvailable && (
                <Badge bg='info' data-cy='partial-availability'>
                  <Trans
                    i18nKey='app.purchase.table.total_partial'
                    values={{
                      count: product.totalAvailableQuantity,
                    }}
                  />
                </Badge>
              )}
              {product.containsFallbackOffers && (
                <Tooltip
                  label={
                    <Trans
                      i18nKey={resolveErrorKey(
                        offer.offerMetadata.providerErrors[0],
                      )}
                    />
                  }
                  enabled={offer.offerMetadata.providerErrors.length > 0}
                >
                  {/* span wrapper is needed because disabled buttons disabled event listeners and thus tooltip won't work */}
                  <span>
                    <IconButton
                      id='refetch-tyre'
                      variant='secondary'
                      icon='reload'
                      iconAlign='right'
                      size='xs'
                      isLoading={manageSearch.refreshEan.isLoading}
                      disabled={!product.retryable}
                      onClick={() => {
                        onRefreshTyre(product.tyre.ean)
                          .then((offers) => {
                            methods.reset({
                              quantity: offers.map(
                                (offer) => offer.offerMetadata.proposedQuantity,
                              ),
                            });
                          })
                          .catch(() => {});
                      }}
                    >
                      <span className='add-to-cart-text'>
                        <Trans i18nKey='app.common.button.relaunch' />
                      </span>
                    </IconButton>
                  </span>
                </Tooltip>
              )}
              <PrivateComponent
                roles={[
                  ROLES.ROLE_UPDATE_CART,
                  ROLES.ROLE_UPDATE_REDISTRIBUTION_CART,
                ]}
                strategy='affirmative'
              >
                {product.canOrder ? (
                  <PurchaseAddToCartButton
                    id={`${product.tyre.ean}-add-to-cart`}
                    form={product.tyre.ean}
                    isLoading={isAdding}
                    productOrderedQuantity={tyreOrderedQuantity}
                    disabled={
                      !product.canOrder ||
                      tyreOrderedQuantity > 0 ||
                      manageSearch.refreshEan.isLoading
                    }
                  />
                ) : (
                  <Tooltip
                    label={<Trans i18nKey='app.common.status.not_orderable' />}
                  >
                    <PurchaseAddToCartButton
                      id={`${product.tyre.ean}-add-to-cart`}
                      form={product.tyre.ean}
                      isLoading={isAdding}
                      productOrderedQuantity={tyreOrderedQuantity}
                      disabled={
                        !product.canOrder ||
                        tyreOrderedQuantity > 0 ||
                        manageSearch.refreshEan.isLoading
                      }
                    />
                  </Tooltip>
                )}
              </PrivateComponent>
              <PrivateComponent
                roles={[
                  ROLES.ROLE_DELETE_CART,
                  ROLES.ROLE_DELETE_REDISTRIBUTION_CART,
                ]}
                strategy='affirmative'
              >
                {cartItems.length > 0 && (
                  <Button
                    id={`${product.tyre.ean}-remove-from-cart`}
                    variant='link'
                    size='xs'
                    isLoading={cart.remove.isLoading}
                    onClick={() =>
                      cart.remove.mutate(
                        cartItems.map((item) => item.id),
                        {
                          onError: async (error) => {
                            if (
                              error.message ===
                              PurchaseErrorCodes.ERR_INVALID_CART
                            ) {
                              await cart.get.refetch();
                            }
                          },
                        },
                      )
                    }
                  >
                    <span className='me-1 remove-text'>
                      <Trans i18nKey='app.common.buttons.remove' />
                    </span>
                    <Icon icon='trash' size='xs' />
                  </Button>
                )}
              </PrivateComponent>
            </>
          </Flex>
          {showStockDetails && (
            <>
              <div
                className='stock-overlay-left rounded'
                onClick={toggleShowStockDetails}
              />
              <div
                className='stock-overlay-right rounded'
                onClick={toggleShowStockDetails}
              />
            </>
          )}
        </td>
      )}
      {detailOpened && (
        <PurchaseDetailModal
          customer={customer}
          product={product.tyre}
          onClose={detailHandlers.setFalse}
        />
      )}
    </tr>
  );
}

export default OfferSuccessRow;
